<template>
    <TprmModal
        additional-class-name="EvaluationRuleModal"
        size="xl"
        open
        :title="modalTitle"
        :subtitle="modalSubtitle"
        @close="onClose"
    >
        <div class="EvaluationRuleModal__content">
            <template v-if="areEnoughAvailableRequirementTypes">
                <div class="mb-6">
                    <div class="font-medium mb-2">
                        Select Requirements <span class="text-eidDangerRed mr-2">*</span>
                        <span class="text-eidDarkBlue-75 font-normal">(Choose 2 or more requirements)</span>
                    </div>
                    <div class="EvaluationRuleModal__cardsContainer">
                        <div class="EvaluationRuleModal__cardsList">
                            <EvaluationRuleModalRequirementCard
                                v-for="option in optionsRequirementTypes"
                                :key="option.id"
                                :option="option"
                                :criteria-count="getCriteriaCount(option.value)"
                                :is-selected="selectedRequirementTypes.includes(option.value)"
                                :is-disabled="disabledRequirementTypes.includes(option.value)"
                                @change="onRequirementSelected"
                            />
                        </div>
                    </div>
                </div>

                <div
                    v-if="selectedRequirementTypes.length > 0"
                    class="EvaluationRuleModal__preview"
                >
                    <div class="font-medium mb-2 text-eidDarkBlue">
                        Evaluation Rule Preview:
                    </div>
                    <div class="text-eidDarkBlue-75">
                        Entities must satisfy
                        <span class="font-semibold"> at least one </span>
                        of the following requirements:
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2 items-center">
                        <template v-for="(type, index) in selectedRequirementTypes" :key="type">
                            <span
                                v-if="index > 0"
                            >OR
                            </span>
                            <span
                                class="EvaluationRuleModal__requirementBadge"
                            >
                                {{ getRequirementTypeLabel(type) }}
                            </span>
                        </template>
                    </div>
                </div>
            </template>
            <template v-else>
                <p class="mb-4">
                    You must have two or more requirements in your risk profile before you can add an evaluation rule.
                </p>
                <p>
                    You can configure this risk profile to evaluate an entity based on the criteria from one of the
                    requirements selected below.
                    Entities must also meet the criteria for any requirements added outside of this rule.
                </p>
            </template>
        </div>
        <template #footer>
            <NewButton
                class="EvaluationRuleModal__secondaryButton"
                data-test-id="EvaluationRuleModal__secondaryButton"
                variant="outline"
                color="danger"
                @click="onSecondaryClicked"
            >
                {{ secondaryBtnText }}
            </NewButton>
            <NewButton
                class="EvaluationRuleModal__primaryButton"
                data-test-id="EvaluationRuleModal__primaryButton"
                color="primary"
                :disabled="!canConfirm"
                @click="onSave"
            >
                Create Rule
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { intersection } from 'lodash';
    import isEqual from 'lodash/isEqual';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import {
        GetRequirementTypeLabel,
    } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import EvaluationRuleModalRequirementCard
        from '@/modules/decisioning-criteria/components/EvaluationRuleModal/sub-components/EvaluationRuleModalRequirementCard.vue';

    type DropdownOptionRequirementType = DropdownOption<TprmRequirementType>;

    export default defineComponent({
        name: 'EvaluationRuleModal',
        components: {
            EvaluationRuleModalRequirementCard,
            NewButton,
            TprmModal,
        },
        props: {
            evaluationRuleRequirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            getRequirementTypeLabel: {
                type: Function as PropType<GetRequirementTypeLabel>,
                required: true,
            },
            criteriaByRequirementType: {
                type: Object as PropType<Partial<Record<TprmRequirementType, { verify: boolean }[]>>>,
                required: true,
            },
            isEvaluationRuleRequirementTypesSelected: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'close', 'delete', 'save' ],
        data() {
            return {
                // if there's no previously saved req, then we don't have anything to delete, just cancel
                secondaryBtnText: this.evaluationRuleRequirementTypes.length > 0
                    ? 'Delete Evaluation Rule'
                    : 'Cancel',
                deleteIconSrc: faXmark,
                selectedRequirementTypes: [] as TprmRequirementType[],
            };
        },
        computed: {
            modalTitle(): string {
                return this.evaluationRuleRequirementTypes.length > 0
                    ? 'Edit Evaluation Rule'
                    : 'Set Evaluation Rule';
            },
            modalSubtitle(): string {
                return this.areEnoughAvailableRequirementTypes
                    ? 'Create an OR rule where entities can satisfy one of the selected requirements instead of all of them.'
                    : '';
            },
            availableRequirementTypes(): TprmRequirementType[] {
                return Object.keys(
                    this.criteriaByRequirementType,
                ) as TprmRequirementType[];
            },
            areEnoughAvailableRequirementTypes(): boolean {
                return this.availableRequirementTypes.length > 1;
            },
            optionsRequirementTypes(): DropdownOptionRequirementType[] {
                return this.mapRequirementTypesToOptions(this.availableRequirementTypes);
            },
            disabledRequirementTypes(): TprmRequirementType[] {
                return Object.entries(this.criteriaByRequirementType)
                    .reduce<TprmRequirementType[]>((result, [ key, criteria ]) => {
                        const allCriteriaNoVerify = criteria?.every(
                            (criterion) => !criterion.verify,
                        );

                        if (allCriteriaNoVerify) {
                            result.push(key as TprmRequirementType);
                        }

                        return result;
                    }, []);
            },
            isModified(): boolean {
                return !isEqual(this.selectedRequirementTypes, this.evaluationRuleRequirementTypes);
            },
            canConfirm(): boolean {
                const hasEnoughReqTypes = this.selectedRequirementTypes.length > 1;
                const selectedDisabledTypes = intersection(
                    this.selectedRequirementTypes,
                    this.disabledRequirementTypes,
                );
                return this.isModified && hasEnoughReqTypes && selectedDisabledTypes.length === 0;
            },
        },
        beforeMount() {
            this.selectedRequirementTypes = [ ...this.evaluationRuleRequirementTypes ];
        },
        methods: {
            mapRequirementTypeToOption(requirementType: TprmRequirementType): DropdownOptionRequirementType {
                let optionLabel = this.getRequirementTypeLabel(requirementType);

                if (this.disabledRequirementTypes.includes(requirementType)) {
                    optionLabel += ' (collect-only)';
                }

                return {
                    id: requirementType,
                    value: requirementType,
                    label: optionLabel,
                };
            },
            mapRequirementTypesToOptions(
                requirementTypes: TprmRequirementType[],
            ): DropdownOptionRequirementType[] {
                return requirementTypes.map((requirementType) => this.mapRequirementTypeToOption(requirementType));
            },
            selectOption(option: DropdownOptionRequirementType, index: number): void {
                const newSelectedRequirementTypes = [ ...this.selectedRequirementTypes ];
                newSelectedRequirementTypes[index] = option.value;
                this.selectedRequirementTypes = newSelectedRequirementTypes;
            },
            onClose(): void {
                this.$emit('close');
            },
            onDelete(): void {
                this.$emit('delete');
                this.$emit('close');
            },
            onSecondaryClicked(): void {
                // if there's no previously saved req, then we don't have anything to delete, just cancel
                if (this.evaluationRuleRequirementTypes.length > 0) {
                    this.onDelete();
                } else {
                    this.onClose();
                }
            },
            onSave(): void {
                this.$emit('save', {
                    requirementTypes: this.selectedRequirementTypes,
                });
                this.onClose();
            },
            getCriteriaCount(requirementType: TprmRequirementType): number {
                return (this.criteriaByRequirementType[requirementType] || []).length;
            },
            onRequirementSelected({ requirement, selected }: {
                requirement: TprmRequirementType;
                selected: boolean;
            }): void {
                if (selected) {
                    this.selectedRequirementTypes = [ ...this.selectedRequirementTypes, requirement ];
                } else {
                    this.selectedRequirementTypes = this.selectedRequirementTypes.filter(
                        (type: TprmRequirementType) => type !== requirement,
                    );
                }
            },
        },
    });
</script>
